import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useUpdateDocument } from "@/hooks/documents/useDocumentMutations";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface PublishGuidesPanelProps {
  genieId: string;
  documents: DocumentWithMetadata[];
}

const answerCount = (document: DocumentWithMetadata): number => {
  const raw = document.metadata?.answers;
  return Array.isArray(raw) ? raw.length : 0;
};

/**
 * Which of this Genie's CREATED documents are published to its public page.
 *
 * Only created documents — the structured, co-branded FAQ / how-to /
 * one-pager pipeline — can be guides. Raw knowledge-base sources are
 * ingestion material (scraped sites, optimisation rewrites, internal notes);
 * they feed the Genie and the document writer, but they are never shown to a
 * stranger, whatever their toggle says. Approved page drafts save into this
 * list; saving changes to a hidden guide does not publish it.
 *
 * This is the same `is_public` field and mutation the document library uses,
 * surfaced where the decision is actually being made.
 */
export const PublishGuidesPanel = ({ genieId, documents }: PublishGuidesPanelProps) => {
  const updateDocument = useUpdateDocument();

  const createdDocuments = useMemo(
    () => documents.filter((document) => document.source_type === "genie-document"),
    [documents],
  );

  const togglePublished = async (document: DocumentWithMetadata, next: boolean) => {
    try {
      await updateDocument.mutateAsync({ document, updates: { is_public: next } });
      toast.success(next ? `“${document.name}” is on your page` : `“${document.name}” is hidden again`);
    } catch {
      toast.error("Couldn't change that document's visibility.");
    }
  };

  if (createdDocuments.length === 0) {
    return (
      <div className="rounded-xl bg-gray-50 px-4 py-4 text-center">
        <p className="text-[13px] leading-snug text-gray-600">
          No created documents yet. Your page shows documents written for the
          public — branded FAQs, how-tos and one-pagers built from this Genie's
          knowledge — never your raw sources. The Genie drafts them itself as
          the knowledge base grows, or you can create one now.
        </p>
        <Link
          to={`/genie-hub/knowledge-base/${genieId}/documents`}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-genie-primary hover:underline"
        >
          <FileText className="h-4 w-4" />
          Create a document
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-[13px] leading-snug text-gray-600">
        These are documents written for the public — raw sources never appear on
        your page. A published document becomes a readable, downloadable guide
        and fills the questions band. Hidden guides are saved here too; switch them on to publish them.
      </p>

      <ul className="divide-y divide-gray-100">
        {createdDocuments.map((document) => {
          const answers = answerCount(document);
          return (
            <li key={document.id} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{document.name}</p>
                <p className="text-[12.5px] text-gray-500">
                  {document.is_public ? "Published" : "Saved — hidden"}{" · "}
                  {answers > 0
                    ? `Answers ${answers} ${answers === 1 ? "question" : "questions"}`
                    : "No questions in this document"}
                </p>
              </div>
              <Switch
                checked={document.is_public === true}
                disabled={updateDocument.isPending}
                onCheckedChange={(checked) => void togglePublished(document, checked)}
                aria-label={`Publish ${document.name}`}
              />
            </li>
          );
        })}
      </ul>

      <Link
        to={`/genie-hub/knowledge-base/${genieId}/documents`}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-genie-primary hover:underline"
      >
        <FileText className="h-4 w-4" />
        Open saved documents to view or edit
      </Link>
    </div>
  );
};

export default PublishGuidesPanel;
